// ════════════════════════════════════════════════════════════════════
// FREE-ENTRY SCORES, AND A SEPARATE PAYLOAD DEFENCE (#372)
//
// Two different rules live here, and keeping them apart is the point of the
// file. Conflating them was the first version of this change.
//
// ── 1. THE PRODUCT RULE: free-form numeric entry ────────────────────
//
// Some formats are scored by typing two numbers with nothing to derive them
// from — padel24 and the americano family. A padel24 match was recorded on a
// real tablet as 70–6 and applied: standings updated, ratings moved ±37, and
// `Number.isFinite` was the whole check, so there was no upper bound and no
// lower one either.
//
// The decided answer is a GENERIC sanity range on those paths: a finite
// INTEGER from 0 to 99 inclusive. Not a realistic maximum — 60–0 is allowed,
// and so is 70–6. What it stops is what cannot be a score at all, and padel
// scores are always whole numbers (#419).
//
// IT DOES NOT APPLY TO SET-BASED FORMATS. Klubbmesterskap and seriespill carry
// a structured score whose validity comes from their OWN scoring rules — sets,
// games, tiebreaks, the format spec. «Every number is ≤ 99» is not a rule
// those formats have, and asserting it here would be a new domain rule nobody
// decided, layered on top of the real one.
//
// ── 2. THE DEFENCE: a payload that is not numbers ───────────────────
//
// Separate from the above, and deliberately weaker. A request body is not a
// keypad: it can carry NaN, Infinity or a negative where a count belongs, and
// no scoring rule in any format makes sense of those. Rejecting them is the
// server declining to compute on nonsense — it says nothing about whether a
// score is legal for its format, and it must never grow into saying so.
//
// If a set-based format should bound its own numbers, that belongs with the
// format's rules and is a separate decision.
// ════════════════════════════════════════════════════════════════════

/** The lowest score a free-entry field may record. */
export const SCORE_MIN = 0;
/** The highest. Two digits is the whole rule. */
export const SCORE_MAX = 99;

/**
 * One FREE-ENTRY score value — `score1` / `score2` on padel24 and americano.
 *
 * Rejects non-numbers, NaN, Infinity, out-of-range values AND fractions, so a
 * caller can use this as the only check on a value it is about to apply.
 *
 * INTEGERS, by decision (#419): padel scores are whole numbers, and 6.5 is not
 * something anyone can enter — it is a hand-built request body, and it would
 * reach the ELO maths and the standings table looking almost right. A
 * fractional value is the same class of mistake as 100, so it gets the same
 * product-rule refusal rather than a code of its own.
 *
 * Not for set-based formats: see the header.
 */
export function isScoreInRange(value: unknown): boolean {
  return (
    typeof value === 'number' && Number.isInteger(value) && value >= SCORE_MIN && value <= SCORE_MAX
  );
}

/**
 * Does a structured score carry a number that cannot be a count at all?
 *
 * THE DEFENCE, NOT THE SCORING RULE. `true` means NaN, Infinity or a negative
 * — never «too high», because how high is too high is the format's business
 * and this function does not know the format.
 *
 * NOR «not a whole number». The free-entry rule requires integers (#419) and
 * this deliberately does not: whether a set-based format's numbers may be
 * fractional is that format's rule, and the decision that made free-entry
 * scores integers was explicitly scoped to free entry.
 *
 * A payload with no numbers in it is NOT nonsensical here. The shapes a
 * structured score can take belong to the formats, and guessing at the key
 * names well enough to declare one empty is how a valid result gets refused by
 * a layer that had no business ruling on it.
 */
export function hasNonsensicalScoreValue(score: unknown): boolean {
  return collectScoreValues(score).some(
    (v) => typeof v === 'number' && (!Number.isFinite(v) || v < 0),
  );
}

/** Numbers a structured score carries, in no particular order. */
function collectScoreValues(score: unknown): unknown[] {
  if (Array.isArray(score)) return score.flatMap(collectScoreValues);
  if (!score || typeof score !== 'object') return [];
  const row = score as Record<string, unknown>;
  const out: unknown[] = [];
  for (const [key, value] of Object.entries(row)) {
    if (key === 'sets' && Array.isArray(value)) out.push(...collectScoreValues(value));
    else if (typeof value === 'number') out.push(value);
  }
  return out;
}

/**
 * Hold a FREE-ENTRY field inside the range as it is typed.
 *
 * Takes and returns the raw string an input holds, because an empty field is a
 * legitimate in-progress state and must survive: clamping '' to '0' would
 * fight the operator as they clear a field to retype it.
 *
 * Convenience at the point of entry, not the guarantee — the server refuses an
 * out-of-range free-entry score whatever the browser did. Set-based entry has
 * its own keypad with its own maximum and does not use this.
 */
export function clampScoreInput(raw: string): string {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return '';
  if (n < SCORE_MIN) return String(SCORE_MIN);
  if (n > SCORE_MAX) return String(SCORE_MAX);
  // Whole numbers only (#419), so the field cannot hold a value the server
  // will refuse. Truncated rather than blanked: someone typing «6.5» meant a 6
  // of something, and emptying the field on the keystroke after the dot is how
  // an input starts feeling broken.
  if (!Number.isInteger(n)) return String(Math.floor(n));
  return raw;
}
